import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { getImportantNotifications, completeNotification } from "requesters/userRequester";
import { getBaseUrl, getCurrentUser, getImportantNotificationsPanelOpen } from "../reducers";
import { setImportantNotificationsPanelOpen } from "../actions";

const BREAKPOINT_DOCKED = 1024;

// Session storage keys for tracking auto-open and viewed notifications
const SESSION_AUTO_OPENED_KEY = "importantNotificationsAutoOpenedThisSession";
const SESSION_VIEWED_IDS_KEY = "importantNotificationsViewedIds";

// Helper to check if we've auto-opened this session
const hasAutoOpenedThisSession = () => {
  try {
    return sessionStorage.getItem(SESSION_AUTO_OPENED_KEY) === "true";
  } catch (e) {
    return false;
  }
};

// Helper to mark that we've auto-opened this session
const markAutoOpenedThisSession = () => {
  try {
    sessionStorage.setItem(SESSION_AUTO_OPENED_KEY, "true");
  } catch (e) {
    // sessionStorage not available, ignore
  }
};

// Helper to get viewed notification IDs from this session
const getViewedIds = () => {
  try {
    const stored = sessionStorage.getItem(SESSION_VIEWED_IDS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
};

// Helper to mark notification IDs as viewed
const markIdsAsViewed = ids => {
  try {
    const current = getViewedIds();
    const updated = [...new Set([...current, ...ids])];
    sessionStorage.setItem(SESSION_VIEWED_IDS_KEY, JSON.stringify(updated));
  } catch (e) {
    // sessionStorage not available
  }
};

const styles = {
  // Docked sidebar for >= 1024px (no backdrop needed)
  // z-index 990 keeps it below calendar dropdowns and navbar dropdowns (Bootstrap default 1000+)
  sidebar: {
    position: "fixed",
    top: "48px",
    right: 0,
    width: "420px",
    height: "calc(100vh - 48px)",
    overflowY: "auto",
    backgroundColor: "#ffffff",
    borderLeft: "1px solid #dee2e6",
    zIndex: 990,
    display: "flex",
    flexDirection: "column"
  },
  // Full-screen overlay for < 1024px
  sidebarFullScreen: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100vh",
    overflowY: "auto",
    backgroundColor: "#ffffff",
    zIndex: 1040,
    display: "flex",
    flexDirection: "column"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "20px 24px",
    borderBottom: "1px solid #e5e5e5",
    backgroundColor: "#f8f9fa"
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: "4px",
    color: "#212529"
  },
  subtitle: {
    fontSize: "12px",
    color: "#6c757d"
  },
  closeButton: {
    background: "transparent",
    border: 0,
    fontSize: "24px",
    lineHeight: 1,
    cursor: "pointer",
    padding: "4px",
    color: "#6c757d",
    transition: "color 0.2s"
  },
  content: {
    padding: "20px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flex: 1,
    overflowY: "auto"
  },
  status: {
    fontSize: "13px",
    color: "#6c757d"
  },
  error: {
    fontSize: "13px",
    color: "#dc3545",
    border: "1px solid #dc3545",
    borderRadius: "6px",
    padding: "8px"
  },
  notificationCard: {
    backgroundColor: "#f9fbff",
    borderRadius: "10px",
    padding: "12px",
    border: "1px solid #e1ebff"
  },
  notificationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: "4px"
  },
  notificationTitle: {
    fontWeight: 600,
    fontSize: "15px",
    marginRight: "8px"
  },
  notificationTimestamp: {
    fontSize: "11px",
    color: "#6c757d"
  },
  notificationBody: {
    fontSize: "13px",
    color: "#343a40",
    marginBottom: "12px"
  },
  notificationActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  linkButton: {
    fontSize: "13px",
    color: "#0056b3",
    textDecoration: "underline"
  },
  completeButton: {
    backgroundColor: "#0069d9",
    color: "#ffffff",
    border: 0,
    borderRadius: "6px",
    padding: "6px 12px",
    fontSize: "13px",
    cursor: "pointer"
  },
  completeButtonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed"
  },
  logSection: {
    borderTop: "1px solid #e5e5e5",
    padding: "16px 24px 24px 24px",
    backgroundColor: "#f8f9fa"
  },
  logHeading: {
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "8px"
  },
  logList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  logItem: {
    fontSize: "12px",
    display: "flex",
    flexDirection: "column"
  },
  logTitle: {
    fontWeight: 500,
    marginBottom: "2px"
  },
  logMeta: {
    color: "#6c757d"
  },
  launcher: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    backgroundColor: "#0069d9",
    color: "#ffffff",
    border: 0,
    borderRadius: "999px",
    padding: "10px 18px",
    fontSize: "14px",
    boxShadow: "0 12px 24px rgba(0, 0, 0, 0.18)",
    cursor: "pointer",
    zIndex: 990
  },
  // Attention-grabbing launcher for unviewed notifications
  launcherAttention: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: 0,
    borderRadius: "999px",
    padding: "12px 20px",
    fontSize: "14px",
    fontWeight: 600,
    boxShadow: "0 12px 24px rgba(220, 53, 69, 0.35)",
    cursor: "pointer",
    zIndex: 990
  },
  launcherIcon: {
    marginRight: "6px"
  }
};

export default function ImportantNotificationsSidebar() {
  const dispatch = useDispatch();
  const baseUrl = useSelector(getBaseUrl);
  const currentUser = useSelector(getCurrentUser);
  const isOpen = useSelector(getImportantNotificationsPanelOpen);

  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completingIds, setCompletingIds] = useState([]);
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth < BREAKPOINT_DOCKED : false
  );
  // Track which notification IDs user has viewed this session
  const [viewedIds, setViewedIds] = useState(() => getViewedIds());

  const hasInitializedRef = useRef(false);
  const previousActiveIdsRef = useRef([]);

  // Handle window resize - auto-close on small screens in docked mode
  useEffect(() => {
    const handleResize = () => {
      const isNowSmall = window.innerWidth < BREAKPOINT_DOCKED;
      setIsSmallScreen(isNowSmall);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const setIsOpen = useCallback(value => {
    dispatch(setImportantNotificationsPanelOpen(value));
  }, [dispatch]);

  const fetchNotifications = useCallback(() => {
    if (!baseUrl) return;

    // Only show loading indicator on initial load, not on background refreshes
    // This prevents the content from jumping when notifications are refreshed
    if (!hasInitializedRef.current) {
      setIsLoading(true);
    }
    getImportantNotifications(baseUrl, { activeOnly: false })
      .then(result => {
        if (result.error) {
          setError("Unable to load important notifications.");
          return;
        }

        const responseData = result?.response?.data || [];
        setNotifications(responseData);
        setError(null);
      })
      .finally(() => {
        setIsLoading(false);
        if (!hasInitializedRef.current) {
          hasInitializedRef.current = true;
        }
      });
  }, [baseUrl]);

  useEffect(() => {
    fetchNotifications();

    const interval = window.setInterval(fetchNotifications, 30000);
    return () => window.clearInterval(interval);
  }, [fetchNotifications]);

  const activeNotifications = useMemo(() => (
    notifications.filter(notification => notification?.attributes?.state !== "completed")
  ), [notifications]);

  const completedNotifications = useMemo(() => (
    notifications
      .filter(notification => notification?.attributes?.state === "completed")
      .sort((a, b) => {
        const aTime = a?.attributes?.completed_at_iso;
        const bTime = b?.attributes?.completed_at_iso;
        const aValue = aTime ? Number(new Date(aTime).getTime()) : 0;
        const bValue = bTime ? Number(new Date(bTime).getTime()) : 0;
        const safeAValue = Number.isNaN(aValue) ? 0 : aValue;
        const safeBValue = Number.isNaN(bValue) ? 0 : bValue;
        return safeBValue - safeAValue;
      })
      .slice(0, 10)
  ), [notifications]);

  useEffect(() => {
    // Only auto-open ONCE per login session (not on every page refresh)
    // Conditions for auto-open:
    // 1. Component hasn't initialized yet (first render)
    // 2. Haven't auto-opened this session yet (sessionStorage check)
    // 3. On a large screen (not mobile)
    // 4. There are active notifications
    if (
      !hasInitializedRef.current
      && !hasAutoOpenedThisSession()
      && !isSmallScreen
      && activeNotifications.length > 0
    ) {
      setIsOpen(true);
      markAutoOpenedThisSession();
      // Also mark current notifications as viewed since we're auto-opening
      markIdsAsViewed(activeNotifications.map(n => String(n.id)));
    }
  }, [activeNotifications, isSmallScreen, setIsOpen]);

  useEffect(() => {
    // Just track active IDs for reference - NO auto-open for new notifications
    // This respects user's choice to keep panel closed
    // The attention indicator on the launcher will alert them instead
    previousActiveIdsRef.current = activeNotifications.map(notification => notification.id);
  }, [activeNotifications]);

  // Mark notifications as viewed when panel is opened
  useEffect(() => {
    if (isOpen && activeNotifications.length > 0) {
      const currentIds = activeNotifications.map(n => String(n.id));
      markIdsAsViewed(currentIds);
      setViewedIds(prev => [...new Set([...prev.map(id => String(id)), ...currentIds])]);
    }
  }, [isOpen, activeNotifications]);

  // Check if there are any unviewed notifications (for attention indicator)
  const hasUnviewedNotifications = useMemo(() => {
    // Convert viewedIds to strings for consistent comparison (IDs from API may be strings or numbers)
    const viewedIdStrings = viewedIds.map(id => String(id));
    return activeNotifications.some(n => !viewedIdStrings.includes(String(n.id)));
  }, [activeNotifications, viewedIds]);

  const handleComplete = useCallback(notificationId => {
    if (!baseUrl || completingIds.includes(notificationId)) {
      return;
    }

    setCompletingIds(prev => [...prev, notificationId]);

    completeNotification(baseUrl, notificationId)
      .then(result => {
        if (result.error) {
          setError("Unable to complete the notification.");
          return;
        }

        const updatedNotification = result?.response?.data;
        if (!updatedNotification) {
          return;
        }

        setNotifications(prev => prev.map(notification => (
          notification.id === updatedNotification.id ? updatedNotification : notification
        )));
        fetchNotifications();
      })
      .finally(() => {
        setCompletingIds(prev => prev.filter(id => id !== notificationId));
      });
  }, [baseUrl, completingIds, fetchNotifications]);

  const hasNotifications = notifications.length > 0;
  const showSidebar = hasNotifications && isOpen;
  const showLauncher = hasNotifications && !isOpen;

  // Use full-screen style on small screens, docked style on large screens
  const sidebarStyle = isSmallScreen ? styles.sidebarFullScreen : styles.sidebar;

  if (!hasNotifications && !isLoading) {
    return null;
  }

  return (
    <>
      {showSidebar && (
        <aside style={sidebarStyle}>
          <header style={styles.header}>
            <div>
              <div style={styles.title}>Important Notifications</div>
              {currentUser?.attributes?.firstname && (
                <div style={styles.subtitle}>{`Hi ${currentUser.attributes.firstname}, stay on top of critical updates.`}</div>
              )}
            </div>
            <button
              type="button"
              style={styles.closeButton}
              onClick={() => setIsOpen(false)}>
              ×
            </button>
          </header>

          <section style={styles.content}>
            {error && (
              <div style={styles.error}>{error}</div>
            )}

            {isLoading && (
              <div style={styles.status}>Loading…</div>
            )}

            {!isLoading && !activeNotifications.length && (
              <div style={styles.status}>No active important notifications.</div>
            )}

            {activeNotifications.map(notification => {
              const {
                body,
                created_at,
                link_url,
                title
              } = notification.attributes;
              const isCompleting = completingIds.includes(notification.id);
              const completeButtonStyle = isCompleting
                ? { ...styles.completeButton, ...styles.completeButtonDisabled }
                : styles.completeButton;

              return (
                <article key={notification.id} style={styles.notificationCard}>
                  <div style={styles.notificationHeader}>
                    <div style={styles.notificationTitle}>{title}</div>
                    <div style={styles.notificationTimestamp}>{created_at}</div>
                  </div>
                  <div style={styles.notificationBody}>{body}</div>
                  <div style={styles.notificationActions}>
                    <a style={styles.linkButton} href={link_url}>
                      View details
                    </a>
                    <button
                      type="button"
                      style={completeButtonStyle}
                      onClick={() => handleComplete(notification.id)}
                      disabled={isCompleting}>
                      {isCompleting ? "Completing…" : "Mark complete"}
                    </button>
                  </div>
                </article>
              );
            })}
          </section>

          {completedNotifications.length > 0 && (
            <section style={styles.logSection}>
              <div style={styles.logHeading}>Completion Log</div>
              <ul style={styles.logList}>
                {completedNotifications.map(notification => {
                  const {
                    completed_at,
                    completed_by_name,
                    title
                  } = notification.attributes;
                  return (
                    <li key={`log-${notification.id}`} style={styles.logItem}>
                      <span style={styles.logTitle}>{title}</span>
                      <span style={styles.logMeta}>
                        {completed_by_name || "Unknown"}
                        {" · "}
                        {completed_at || ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </aside>
      )}

      {showLauncher && (
        <button
          type="button"
          style={hasUnviewedNotifications ? styles.launcherAttention : styles.launcher}
          className={hasUnviewedNotifications ? "important-notifications-launcher--attention" : ""}
          onClick={() => setIsOpen(true)}>
          {hasUnviewedNotifications && (
            <span role="img" aria-label="Alert" style={styles.launcherIcon}>🔔</span>
          )}
          {hasUnviewedNotifications
            ? `${activeNotifications.length} Important Notification${activeNotifications.length !== 1 ? "s" : ""}`
            : `Notifications (${activeNotifications.length})`}
        </button>
      )}
    </>
  );
}
